/**
 * Admin: member fields (custom fields for all of CCMS). Route: /admin/members/custom-fields
 * (Admin role or the Member.CustomFields.Manage right; identity 011).
 *
 * A field is defined once and is then available on every member's profile (Contact tab, More details): a member
 * only shows the fields that have a value. Types come from the shared System catalogue (text, number, date,
 * choice lists and more). Sensitive fields are shown only to people who may edit members. A field with values
 * cannot be deleted (switch it off instead) and its type cannot change; its key never changes, because other
 * services (the call centre) refer to fields by key.
 * API: api/member-custom-fields (MemberCustomFieldService).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconArrowLeft, IconPlus, IconRefresh, IconEdit, IconTrash, IconArrowUp, IconArrowDown, IconLock, IconAlertCircle, IconListDetails,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import {
  Stack, Group, Text, Button, Card, Paper, TextInput, Textarea, Select, Switch, Badge, Alert, Modal, Tooltip, ActionIcon, SimpleGrid, Code,
  EmptyState,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import { PageHelpButton } from '@/components/help';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import {
  useMemberCustomFieldService,
  type MemberCustomFieldDefinitionView,
  type CreateMemberCustomFieldRequest,
  type UpdateMemberCustomFieldRequest,
} from '@actschurch/ccms-sdk';
import { errorMessage } from '@/components/members';
import { MEMBER_CUSTOM_FIELD_DEFINITIONS_KEY } from '@/components/members/tabs/MoreDetailsCard';
import { parseChoices, suggestFieldKey, FIELD_KEY_RE } from '@/components/members/memberDetailsFormat';

const ADMIN_KEY = ['members', 'custom-field-definitions', 'admin'] as const;
const TYPES_KEY = ['members', 'custom-field-types'] as const;
const PAGE = 'members.custom-fields';

type Row = MemberCustomFieldDefinitionView & { id: number };

interface FieldForm {
  label: string;
  fieldKey: string;
  fieldTypeID: string | null;
  category: string;
  helpText: string;
  placeholder: string;
  choices: string;
  allowMultiple: boolean;
  isSensitive: boolean;
  isActive: boolean;
}

const EMPTY_FORM: FieldForm = {
  label: '', fieldKey: '', fieldTypeID: '1', category: '', helpText: '', placeholder: '', choices: '',
  allowMultiple: false, isSensitive: false, isActive: true,
};

export default function MemberCustomFieldsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const service = useMemberCustomFieldService();

  const fields = useQuery({ queryKey: ADMIN_KEY, queryFn: () => service.getAll({ includeInactive: true, withCounts: true }) });
  const types = useQuery({ queryKey: TYPES_KEY, queryFn: () => service.getTypes(), staleTime: 60 * 60 * 1000 });

  const [search, setSearch] = useState('');
  const [showOff, setShowOff] = useState(true);
  const [editing, setEditing] = useState<{ field: MemberCustomFieldDefinitionView | null } | null>(null);
  const [form, setForm] = useState<FieldForm>(EMPTY_FORM);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
    // The profiles' "Add a field" list.
    queryClient.invalidateQueries({ queryKey: MEMBER_CUSTOM_FIELD_DEFINITIONS_KEY, exact: true });
  };

  const typeOf = (id: string | number | null | undefined) => types.data?.find((t) => String(t.typeID) === String(id));
  const formType = typeOf(form.fieldTypeID);
  const isChoice = formType?.code === 'select' || formType?.code === 'multiselect';
  const choices = parseChoices(form.choices);

  const save = useMutation({
    mutationFn: async () => {
      const common = {
        label: form.label.trim(),
        fieldTypeID: Number(form.fieldTypeID),
        category: form.category.trim(),
        helpText: form.helpText.trim(),
        placeholder: form.placeholder.trim(),
        options: isChoice ? choices : [],
        allowMultiple: formType?.code === 'multiselect' ? true : form.allowMultiple,
        isSensitive: form.isSensitive,
        isActive: form.isActive,
      };
      if (editing?.field) {
        const f = editing.field;
        const body: UpdateMemberCustomFieldRequest = { ...common };
        // Only send the type when it changed (the API refuses a change while members have values).
        if (common.fieldTypeID === f.fieldTypeID) delete body.fieldTypeID;
        return service.update(f.customFieldID, body);
      }
      const body: CreateMemberCustomFieldRequest = {
        ...common,
        fieldKey: form.fieldKey.trim() || null,
        category: common.category || null,
        helpText: common.helpText || null,
        placeholder: common.placeholder || null,
      };
      return service.create(body);
    },
    onSuccess: (f) => { toast.success(editing?.field ? 'Field saved' : 'Field added', f.label); setEditing(null); refresh(); },
    onError: (e) => toast.error('Could not save the field', errorMessage(e)),
  });

  const toggleActive = useMutation({
    mutationFn: (f: MemberCustomFieldDefinitionView) => service.update(f.customFieldID, { isActive: !f.isActive }),
    onSuccess: (f) => { toast.success(f.isActive ? 'Field switched on' : 'Field switched off', f.label); refresh(); },
    onError: (e) => toast.error('Could not change the field', errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (f: MemberCustomFieldDefinitionView) => service.delete(f.customFieldID),
    onSuccess: () => { toast.success('Field deleted'); refresh(); },
    onError: (e) => toast.error('Could not delete the field', errorMessage(e)),
  });

  const reorder = useMutation({
    mutationFn: (ids: number[]) => service.reorder(ids),
    onSuccess: (list) => { queryClient.setQueryData(ADMIN_KEY, list); queryClient.invalidateQueries({ queryKey: MEMBER_CUSTOM_FIELD_DEFINITIONS_KEY, exact: true }); },
    onError: (e) => toast.error('Could not change the order', errorMessage(e)),
  });

  const all = fields.data ?? [];
  const ordered = useMemo(() => [...all].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)), [all]);
  const rows: Row[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ordered
      .filter((f) => showOff || f.isActive)
      .filter((f) => !q || f.label.toLowerCase().includes(q) || f.fieldKey.includes(q) || (f.category ?? '').toLowerCase().includes(q))
      .map((f) => ({ ...f, id: f.customFieldID }));
  }, [ordered, search, showOff]);

  const move = (f: MemberCustomFieldDefinitionView, delta: -1 | 1) => {
    const ids = ordered.map((x) => x.customFieldID);
    const i = ids.indexOf(f.customFieldID);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate(ids);
  };

  const openAdd = () => { setForm(EMPTY_FORM); save.reset(); setEditing({ field: null }); };
  const openEdit = (f: MemberCustomFieldDefinitionView) => {
    setForm({
      label: f.label, fieldKey: f.fieldKey, fieldTypeID: String(f.fieldTypeID), category: f.category ?? '', helpText: f.helpText ?? '',
      placeholder: f.placeholder ?? '', choices: (f.options ?? []).join('\n'), allowMultiple: f.allowMultiple, isSensitive: f.isSensitive, isActive: f.isActive,
    });
    save.reset();
    setEditing({ field: f });
  };

  const categories = useMemo(() => [...new Set(all.map((f) => f.category?.trim()).filter((c): c is string => !!c))].sort(), [all]);
  const keyError = !editing?.field && form.fieldKey.trim() && !FIELD_KEY_RE.test(form.fieldKey.trim())
    ? 'Lower-case letters, numbers and _ only, starting with a letter'
    : null;
  const hasValues = (editing?.field?.memberCount ?? 0) > 0;
  const canSave = !!form.label.trim() && !!form.fieldTypeID && !keyError && (!isChoice || choices.length > 0);

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'sortOrder', label: 'Order', width: 90,
      render: (_, f) => (
        <Group gap={2} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
          <ActionIcon variant="subtle" size="sm" aria-label="Move up" disabled={ordered[0]?.customFieldID === f.customFieldID || reorder.isPending} onClick={() => move(f, -1)}>
            <IconArrowUp size={14} />
          </ActionIcon>
          <ActionIcon variant="subtle" size="sm" aria-label="Move down" disabled={ordered[ordered.length - 1]?.customFieldID === f.customFieldID || reorder.isPending} onClick={() => move(f, 1)}>
            <IconArrowDown size={14} />
          </ActionIcon>
        </Group>
      ),
    },
    {
      key: 'label', label: 'Field',
      render: (_, f) => (
        <Stack gap={0}>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={600}>{f.label}</Text>
            {f.isSensitive && <Tooltip label="Shown only to people who may edit members"><Badge size="xs" color="red" variant="light" leftSection={<IconLock size={10} />}>Sensitive</Badge></Tooltip>}
          </Group>
          <Text size="xs" c="dimmed" ff="monospace">{f.fieldKey}</Text>
        </Stack>
      ),
    },
    {
      key: 'fieldTypeName', label: 'Type', width: 150,
      render: (_, f) => (
        <Stack gap={0}>
          <Text size="sm">{f.fieldTypeName ?? typeOf(f.fieldTypeID)?.name ?? `Type ${f.fieldTypeID}`}</Text>
          {f.allowMultiple && f.fieldTypeCode !== 'multiselect' && <Text size="xs" c="dimmed">Several values</Text>}
          {(f.options?.length ?? 0) > 0 && <Text size="xs" c="dimmed">{f.options.length} {f.options.length === 1 ? 'choice' : 'choices'}</Text>}
        </Stack>
      ),
    },
    { key: 'category', label: 'Category', width: 150, render: (_, f) => <Text size="sm" c={f.category ? undefined : 'dimmed'}>{f.category || 'None'}</Text> },
    { key: 'memberCount', label: 'Members', width: 100, render: (_, f) => <Text size="sm">{(f.memberCount ?? 0).toLocaleString('en-ZA')}</Text> },
    {
      key: 'isActive', label: 'On', width: 80,
      render: (_, f) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Tooltip label={f.isActive ? 'On: can be added to members' : 'Off: kept on members, cannot be added'}>
            <Switch size="sm" checked={f.isActive} disabled={toggleActive.isPending} aria-label={`Switch ${f.label} ${f.isActive ? 'off' : 'on'}`}
              onChange={() => toggleActive.mutate(f)} />
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'actions', label: '', width: 90,
      render: (_, f) => (
        <Group gap={2} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
          <Tooltip label="Edit"><ActionIcon variant="subtle" aria-label={`Edit ${f.label}`} onClick={() => openEdit(f)}><IconEdit size={16} /></ActionIcon></Tooltip>
          <Tooltip label={(f.memberCount ?? 0) > 0 ? 'Members have values: switch it off instead' : 'Delete'}>
            <ActionIcon variant="subtle" color="red" aria-label={`Delete ${f.label}`} disabled={(f.memberCount ?? 0) > 0}
              onClick={() => modal.confirm({
                title: `Delete ${f.label}?`,
                body: 'No member has a value for it. It will be removed for everyone.',
                confirmLabel: 'Delete',
                confirmColor: 'red',
                onConfirm: () => remove.mutate(f),
              })}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ),
    },
  ];

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" align="flex-start">
            <Stack gap={4}>
              <Button variant="subtle" size="xs" px={0} leftSection={<IconArrowLeft size={14} />} onClick={() => navigate('/admin/members')}>Back to members</Button>
              <Text size="xl" fw={700}>Member fields</Text>
              <Text c="dimmed" size="sm">
                Extra fields for every member in CCMS. A member only shows the fields that have a value, on the Contact tab under More details.
              </Text>
            </Stack>
            <Group gap="sm" data-help-id={`${PAGE}.actions`}>
              <Tooltip label="Refresh">
                <ActionIcon variant="subtle" size="lg" aria-label="Refresh" loading={fields.isFetching && !fields.isLoading} onClick={refresh}><IconRefresh size={18} /></ActionIcon>
              </Tooltip>
              <Button leftSection={<IconPlus size={16} />} onClick={openAdd} data-help-id={`${PAGE}.new`}>Add a field</Button>
              <PageHelpButton pageKey={PAGE} />
            </Group>
          </Group>

          <Paper withBorder p="md" radius="md" data-help-id={`${PAGE}.toolbar`}>
            <Group gap="md" wrap="wrap" justify="space-between">
              <TextInput style={{ flex: 1, minWidth: 220 }} placeholder="Search label, key or category" value={search}
                onChange={(e) => setSearch(e.currentTarget.value)} aria-label="Search fields" />
              <Switch label="Show fields that are switched off" checked={showOff} onChange={(e) => setShowOff(e.currentTarget.checked)} />
            </Group>
          </Paper>

          {fields.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the member fields">{errorMessage(fields.error)}</Alert>}

          {!fields.isLoading && !fields.isError && all.length === 0 ? (
            <Card withBorder padding="lg" radius="md">
              <EmptyState icon={<IconListDetails size={36} />} title="No member fields yet"
                description="Add a field, for example an alternative WhatsApp number or a baptism date. It is then available on every member."
                actionLabel="Add a field" onAction={openAdd} />
            </Card>
          ) : (
            <Card withBorder p={0} data-help-id={`${PAGE}.list`}>
              <DataTable<Row>
                columns={columns}
                data={rows}
                loading={fields.isLoading}
                striped
                highlightOnHover
                enableInternalSearch={false}
                enableInternalSort={false}
                onRowClick={(f) => openEdit(f)}
                emptyMessage={search ? 'No field matches the search' : 'No fields to show'}
              />
            </Card>
          )}
        </Stack>
      </FadeIn>

      <Modal opened={!!editing} onClose={() => setEditing(null)} centered size="lg" title={editing?.field ? `Edit ${editing.field.label}` : 'Add a member field'}>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Label" required placeholder="For example Alternative WhatsApp number" value={form.label} data-autofocus
              onChange={(e) => setForm({ ...form, label: e.currentTarget.value })} />
            {editing?.field ? (
              <TextInput label="Key" value={form.fieldKey} disabled description="Other services use the key, so it never changes." />
            ) : (
              <TextInput label="Key" placeholder={form.label.trim() ? suggestFieldKey(form.label) : 'Made from the label'} value={form.fieldKey}
                error={keyError} description="Leave blank to make it from the label."
                onChange={(e) => setForm({ ...form, fieldKey: e.currentTarget.value.toLowerCase() })} />
            )}
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select label="Type" required allowDeselect={false} value={form.fieldTypeID}
              data={(types.data ?? []).map((t) => ({ value: String(t.typeID), label: t.name }))}
              disabled={hasValues}
              description={hasValues ? 'Members have values, so the type cannot change.' : undefined}
              onChange={(v) => setForm({ ...form, fieldTypeID: v })} />
            <TextInput label="Category" placeholder={categories.length > 0 ? `For example ${categories.slice(0, 2).join(' or ')}` : 'For example Faith journey'}
              description="Groups fields on the profile." value={form.category} maxLength={100}
              onChange={(e) => setForm({ ...form, category: e.currentTarget.value })} />
          </SimpleGrid>
          {isChoice && (
            <Textarea label="Choices" required autosize minRows={3} maxRows={10} placeholder={'One per line, for example\nSmall\nMedium\nLarge'}
              description={choices.length > 0 ? `${choices.length} ${choices.length === 1 ? 'choice' : 'choices'}` : 'Add at least one choice.'}
              value={form.choices} onChange={(e) => setForm({ ...form, choices: e.currentTarget.value })} />
          )}
          <TextInput label="Help text" placeholder="Shown under the field when someone fills it in" maxLength={500} value={form.helpText}
            onChange={(e) => setForm({ ...form, helpText: e.currentTarget.value })} />
          <TextInput label="Placeholder" placeholder="Hint inside an empty input" maxLength={200} value={form.placeholder}
            onChange={(e) => setForm({ ...form, placeholder: e.currentTarget.value })} />
          <Stack gap="xs">
            {formType?.code !== 'multiselect' && (
              <Switch label="Allow several values" description="For example more than one alternative number." checked={form.allowMultiple}
                onChange={(e) => setForm({ ...form, allowMultiple: e.currentTarget.checked })} />
            )}
            <Switch label="Sensitive" color="red" description="Only people who may edit members can see it. Its values are never written to the activity history."
              checked={form.isSensitive} onChange={(e) => setForm({ ...form, isSensitive: e.currentTarget.checked })} />
            <Switch label="On" description="Switched off: kept on members but cannot be added." checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })} />
          </Stack>
          {editing?.field && (
            <Text size="xs" c="dimmed">
              Key <Code>{editing.field.fieldKey}</Code>. {(editing.field.memberCount ?? 0).toLocaleString('en-ZA')} {editing.field.memberCount === 1 ? 'member has' : 'members have'} a value.
            </Text>
          )}
          {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
            <Button loading={save.isPending} disabled={!canSave} onClick={() => save.mutate()}>{editing?.field ? 'Save' : 'Add field'}</Button>
          </Group>
        </Stack>
      </Modal>
    </LayoutContainer>
  );
}
